"use client";

import React from "react";
import { Target, TrendingUp, Megaphone, Trophy, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const roadmapSteps = [{
  id: 1,
  number: "01",
  title: "Evaluation",
  description: "Comprehensive assessment of your current skill level, hockey IQ, and physical attributes to establish a baseline for DB Hockey Advancement.",
  icon: Target
}, {
  id: 2,
  number: "02",
  title: "Development",
  description: "Customized training programs and personalized mentorship designed to address weaknesses, enhance strengths, and elevate your overall game.",
  icon: TrendingUp
}, {
  id: 3,
  number: "03",
  title: "Promotion",
  description: "Creation of high-impact player profiles and highlight videos, strategically distributed to our extensive network of scouts and coaches.",
  icon: Megaphone
}, {
  id: 4,
  number: "04",
  title: "Placement",
  description: "Strategic guidance and direct advocacy to secure the best possible opportunities in top-tier junior and college hockey programs.",
  icon: Trophy
}];
function StepCard({
  step,
  index
}: {
  step: typeof roadmapSteps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 !== 0;
  const Icon = step.icon;
  return <div ref={ref} data-index={index} className={`relative flex flex-col md:flex-row items-start md:items-center justify-between mb-16 last:mb-0 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} ${isEven ? "md:flex-row-reverse" : ""}`}>
      {}
      <div className="absolute left-0 md:left-1/2 w-10 h-10 bg-background border-2 border-primary rounded-none rotate-45 -translate-x-[19px] md:-translate-x-1/2 flex items-center justify-center z-10 shadow-[0_0_20px_color-mix(in_srgb,var(--color-primary),transparent_60%)] mt-4 md:mt-0">
        <div className="w-3 h-3 bg-primary -rotate-45 shadow-[0_0_10px_color-mix(in_srgb,var(--color-primary),transparent_20%)]"></div>
      </div>

      {}
      <div className={`w-full md:w-[45%] pl-12 md:pl-0 ${isEven ? "md:pr-12" : "md:pl-12"}`}>
        <Card className="bg-card/30 backdrop-blur-xl border-border/40 rounded-sm relative overflow-hidden group hover:border-primary/50 transition-colors duration-500">
          {}
          <div className="absolute top-0 right-0 w-24 h-24 bg-primary/10 -translate-y-12 translate-x-12 rotate-45 group-hover:bg-primary/20 transition-colors duration-500 pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-16 h-16 bg-primary/5 translate-y-8 -translate-x-8 rotate-45 pointer-events-none"></div>

          <CardHeader className="pb-4 relative z-10">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-primary/10 border border-primary/20 rounded-sm">
                <Icon className="w-6 h-6 text-primary" />
              </div>
              <span className="text-5xl font-black text-primary/10 select-none tracking-tighter">
                {step.number}
              </span>
            </div>
            <CardTitle className="text-2xl font-bold tracking-tight text-foreground uppercase">
              {step.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="relative z-10">
            <CardDescription className="text-base text-muted-foreground leading-relaxed">
              {step.description}
            </CardDescription>
          </CardContent>
          
          {}
          <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-primary group-hover:w-full transition-all duration-500 ease-out"></div>
        </Card>
      </div>

      {}
      <div className="hidden md:block w-[45%]"></div>
    </div>;
}
export default function TheRoadmapToJuniorCollege() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-roadmap-to-junior-college" className="dark bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="max-w-6xl mx-auto relative z-10">
        {}
        <div ref={headerRef} className={`text-center mb-20 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6 uppercase tracking-wider">
            <ChevronRight className="w-4 h-4" />
            Our Process
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter uppercase mb-6">
            The Roadmap to <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/60">
              Junior & College
            </span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            A systematic, proven approach by DB Hockey Advancement to elevate your game and secure your future at the next level.
          </p>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute left-[19px] md:left-1/2 top-4 bottom-4 w-px border-l-2 border-dashed border-primary/20 md:-translate-x-1/2"></div>

          {}
          <div className="relative z-10">
            {roadmapSteps.map((step, index) => <StepCard key={step.id} step={step} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}